<template>
  <div
    id="behaviour_tab"
    class="flex flex-col gap-6 proxima_nova max-w-4xl mx-auto w-full h-full overflow-y-auto"
  >
    <SkeletonLoader
      v-if="isLoadingData"
      :page="loaderPages.VOICE_AGENT_BEHAVIOUR"
    />
    <template v-else>
      <VoiceAgentSectionHeader
        :title="tabHeaders.behaviour.title"
        :description="tabHeaders.behaviour.description"
      />

      <div class="flex flex-row gap-2 items-end">
        <div class="flex-1">
          <VoiceAgentSelect
            :voices="voices"
            :selected-voice="formData.voice"
            @update:selected-voice="(voice) => updateFormField('voice', voice)"
          />
        </div>
        <VoiceAgentAdjustVoiceDropdown
          :agent-id="agentId || formData.agent_id"
          :selected-voice="formData.voice"
          :selected-language="formData.language"
          @create-agent="handleCreateAgentForVoiceSettings"
        />
      </div>

      <VoiceAgentSelect
        type="language"
        :voices="languages"
        :selected-voice="formData.language"
        @update:selected-voice="
          (language) => language && updateFormField('language', language)
        "
      />

      <div class="form_col gap-2">
        <label for="agent_role_textarea" class="secondary_color text-[13px]"
          >Agent Role and Personality</label
        >
        <a-textarea
          ref="rolePersonalityTextarea"
          id="agent_role_textarea"
          :value="formData.role_personality"
          @change="
            (e) =>
              updateFormField(
                BehaviourFormFields.ROLE_PERSONALITY,
                e.target.value
              )
          "
          @click="saveCursorPosition(BehaviourFormFields.ROLE_PERSONALITY)"
          @keyup="saveCursorPosition(BehaviourFormFields.ROLE_PERSONALITY)"
          :rows="4"
          :placeholder="BehaviourFormPlaceholders.rolePersonality"
          class="textarea_large"
        />
        <div class="flex justify-end">
          <VoiceAgentPersonalizationDropdown
            v-if="formData.agent_id"
            ref="rolePersonalityDropdownRef"
            :agent-id="formData.agent_id"
            @apply-personalization="
              (variableName) =>
                insertVariable(
                  BehaviourFormFields.ROLE_PERSONALITY,
                  variableName
                )
            "
          />
        </div>
      </div>

      <div class="form_col gap-2">
        <label
          for="agent_instructions_textarea"
          class="secondary_color text-[13px]"
          >Add your agent's call flow instructions</label
        >
        <a-textarea
          ref="callFlowInstructionsTextarea"
          id="agent_instructions_textarea"
          :value="formData.call_flow_instructions"
          @change="
            (e) =>
              updateFormField(
                BehaviourFormFields.CALL_FLOW_INSTRUCTIONS,
                e.target.value
              )
          "
          @click="
            saveCursorPosition(BehaviourFormFields.CALL_FLOW_INSTRUCTIONS)
          "
          @keyup="
            saveCursorPosition(BehaviourFormFields.CALL_FLOW_INSTRUCTIONS)
          "
          :rows="4"
          :placeholder="BehaviourFormPlaceholders.callFlowInstructions"
          class="textarea_large"
        />
        <div class="flex justify-end">
          <VoiceAgentPersonalizationDropdown
            v-if="formData.agent_id"
            ref="callFlowDropdownRef"
            :agent-id="formData.agent_id"
            @apply-personalization="
              (variableName) =>
                insertVariable(
                  BehaviourFormFields.CALL_FLOW_INSTRUCTIONS,
                  variableName
                )
            "
          />
        </div>
      </div>
      <div class="form_col gap-2">
        <label for="manage_variables_label" class="secondary_color text-[13px]"
          >Dynamic Variables for agent to use</label
        >
        <CustomContainer
          class="manage_variables_container cursor-pointer"
          @click="handleManageVariablesClick"
        >
          <div class="flex flex-row gap-2 items-center w-fit shrink-0">
            <ThemeImage
              :icon="imageNames.CURLY_BRACE_GREY"
              alt="curly_brace_icon"
              class="size-4"
            />
            <span class="text-color">Manage Variables</span>
          </div>
        </CustomContainer>
      </div>
    </template>

    <VoiceAgentDynamicVariable
      :visible="showManageVariablesModal"
      :agent-id="agentId || formData.agent_id"
      @update:visible="showManageVariablesModal = $event"
      @variables-updated="handleVariablesUpdated"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import { useRoute } from "vue-router";
import { message } from "ant-design-vue";
import { isEqual, debounce } from "lodash";
import VoiceAgentSectionHeader from "@/src/components/voice_agents/VoiceAgentSectionHeader.vue";
import VoiceAgentSelect from "@/src/components/voice_agents/VoiceAgentSelect.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import VoiceAgentService from "@/src/api/VoiceAgentService.js";
import ThemeImage from "@/src/components/ThemeImage.vue";
import VoiceAgentDynamicVariable from "@/src/components/voice_agents/VoiceAgentDynamicVariable.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import {
  BehaviourFormPlaceholders,
  BehaviourFormFields,
  tabHeaders,
  languageDetails,
} from "@/src/constants/voiceAgentConstants.js";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import {
  fetchVoices,
  mapLanguagesWithFlags,
} from "@/src/utils/voiceAgentUtils.js";
import { fetchCountries } from "@/src/utils/utils.js";
import CustomContainer from "@/src/components/CustomContainer.vue";
import VoiceAgentPersonalizationDropdown from "@/src/components/voice_agents/VoiceAgentPersonalizationDropdown.vue";
import VoiceAgentAdjustVoiceDropdown from "@/src/components/voice_agents/VoiceAgentAdjustVoiceDropdown.vue";

const route = useRoute();

const props = defineProps({
  formData: {
    type: Object,
    default: () => ({}),
  },
  isLoadingData: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:formData", "create-success"]);

const voices = ref([]);
const isCreating = ref(false);
const isPublishing = ref(false);
const behaviourFormEdited = ref(false);
const originalFormData = ref(null);
const countryOptions = ref([]);
const countriesLoading = ref(false);
const showManageVariablesModal = ref(false);

const rolePersonalityTextarea = ref(null);
const callFlowInstructionsTextarea = ref(null);
const rolePersonalityDropdownRef = ref(null);
const callFlowDropdownRef = ref(null);
const lastCursorPosition = ref({
  [BehaviourFormFields.ROLE_PERSONALITY]: 0,
  [BehaviourFormFields.CALL_FLOW_INSTRUCTIONS]: 0,
});

const agentId = computed(() => route.params?.id);
const isEditMode = computed(() => !!agentId.value);

const isFormValid = computed(() => {
  try {
    return (
      props.formData?.voice?.voice_id && props.formData?.language?.voice_id
    );
  } catch (error) {
    console.error("Error checking form validity:", error);
    return false;
  }
});

const languages = computed(() => {
  try {
    return mapLanguagesWithFlags(languageDetails, countryOptions.value);
  } catch (error) {
    console.error("Error mapping languages:", error);
    return [];
  }
});

const loadVoices = async () => {
  try {
    const voicesData = await fetchVoices();
    if (voicesData && voicesData.length > 0) {
      voices.value = voicesData;
    }
  } catch (error) {
    console.error("Error fetching voices:", error);
  }
};

const updateFormField = (field, value) => {
  try {
    emit("update:formData", {
      ...props.formData,
      [field]: value,
    });
  } catch (error) {
    console.error(`Error updating ${field}:`, error);
  }
};

const handleFormDataChange = () => {
  try {
    if (originalFormData.value) {
      behaviourFormEdited.value = !isEqual(
        props.formData,
        originalFormData.value
      );

      if (isEditMode.value && behaviourFormEdited.value) {
        debouncedPublish();
      }
    }
  } catch (error) {
    console.error("Error in formData watcher:", error);
  }
};

const handleAgentIdChange = (newAgentId) => {
  try {
    if (newAgentId && !originalFormData.value) {
      originalFormData.value = { ...props.formData };
    }
  } catch (error) {
    console.error("Error in handleAgentIdChange:", error);
  }
};

const handleCreateAgentForVoiceSettings = async (resolve) => {
  try {
    const result = await handleCreateAgentBehaviour();
    resolve(result);
  } catch (error) {
    console.error("Error creating agent for voice settings:", error);
    resolve(false);
  }
};

const handlePublish = async () => {
  try {
    if (!behaviourFormEdited.value) {
      return;
    }

    isPublishing.value = true;

    const payload = {
      voice_id: props.formData?.voice?.voice_id,
      language_id: props.formData?.language?.voice_id,
      agent_role: props.formData?.role_personality?.trim() || "",
      agent_instruction: props.formData?.call_flow_instructions?.trim() || "",
    };

    if (props.formData?.name?.trim()) {
      payload.agent_name = props.formData?.name?.trim();
    }

    const response = await VoiceAgentService.updateAgentBehaviour(
      agentId.value || props.formData?.agent_id,
      payload
    );

    if (response?.success) {
      behaviourFormEdited.value = false;
      originalFormData.value = { ...props.formData };
    }
  } catch (error) {
    console.error("Error publishing behaviour:", error);
    message.error("Failed to publish behaviour");
  } finally {
    isPublishing.value = false;
  }
};

const debouncedPublish = debounce(async () => {
  await handlePublish();
}, 3000);

const handleDiscard = () => {
  try {
    if (originalFormData.value) {
      emit("update:formData", { ...originalFormData.value });
      behaviourFormEdited.value = false;
    }
  } catch (error) {
    console.error("Error discarding changes:", error);
  }
};

const handleManageVariablesClick = async () => {
  try {
    if (agentId.value || props.formData?.agent_id) {
      showManageVariablesModal.value = true;
      return;
    }

    if (!isFormValid.value) {
      return;
    }

    const result = await handleCreateAgentBehaviour();
    if (result?.agent_id || result === true) {
      showManageVariablesModal.value = true;
    }
  } catch (error) {
    console.error("Error handling manage variables click:", error);
    message.error("Failed to create agent. Please try again.");
  }
};

const handleVariablesUpdated = () => {
  try {
    if (rolePersonalityDropdownRef.value?.refreshVariables) {
      rolePersonalityDropdownRef.value.refreshVariables();
    }
    if (callFlowDropdownRef.value?.refreshVariables) {
      callFlowDropdownRef.value.refreshVariables();
    }
  } catch (error) {
    console.error("Error refreshing personalization dropdowns:", error);
  }
};

const handleCreateAgentBehaviour = async () => {
  try {
    if (props.formData?.agent_id) {
      if (behaviourFormEdited.value) {
        await handlePublish();
      }
      return {
        agent_id: props.formData.agent_id,
      };
    }
    isCreating.value = true;

    const agentName = props.formData?.name?.trim() || "Untitled Agent";

    const payload = {
      agent_name: agentName,
      voice_id: props.formData?.voice?.voice_id,
      language_id: props.formData?.language?.voice_id,
      agent_role: props.formData?.role_personality?.trim() || "",
      agent_instruction: props.formData?.call_flow_instructions?.trim() || "",
    };

    const response = await VoiceAgentService.createAgentBehaviour(payload);

    if (response?.success && response?.data) {
      emit("update:formData", {
        ...props.formData,
        agent_id: response.data.voice_agent_id,
      });
      return {
        agent_id: response.data.voice_agent_id,
      };
    }
    message.error("Failed to create agent");
    return false;
  } catch (error) {
    console.error("Error creating agent:", error);
    throw new Error("Failed to create agent");
  } finally {
    isCreating.value = false;
  }
};

const saveCursorPosition = (fieldName) => {
  try {
    const textareaRef =
      fieldName === BehaviourFormFields.ROLE_PERSONALITY
        ? rolePersonalityTextarea.value
        : callFlowInstructionsTextarea.value;

    if (!textareaRef) {
      return;
    }

    const textarea = textareaRef.resizableTextArea?.textArea || textareaRef;

    if (textarea && typeof textarea.selectionStart === "number") {
      lastCursorPosition.value[fieldName] = textarea.selectionStart;
    }
  } catch (error) {
    console.error("Error saving cursor position:", error);
  }
};

const insertVariable = (fieldName, variableName) => {
  try {
    const textareaRef =
      fieldName === BehaviourFormFields.ROLE_PERSONALITY
        ? rolePersonalityTextarea.value
        : callFlowInstructionsTextarea.value;

    if (!textareaRef) {
      return;
    }

    const textarea = textareaRef.resizableTextArea?.textArea || textareaRef;

    if (!textarea) {
      console.error("Invalid textarea reference");
      return;
    }

    const cursorPosition = lastCursorPosition.value[fieldName] || 0;
    const currentValue = props.formData[fieldName] || "";
    const variableText = `{{${variableName}}}`;

    const newValue =
      currentValue.substring(0, cursorPosition) +
      variableText +
      currentValue.substring(cursorPosition);

    updateFormField(fieldName, newValue);

    setTimeout(() => {
      const newCursorPosition = cursorPosition + variableText.length;
      textarea.focus();
      textarea.setSelectionRange(newCursorPosition, newCursorPosition);
      lastCursorPosition.value[fieldName] = newCursorPosition;
    }, 0);
  } catch (error) {
    console.error("Error inserting variable:", error);
  }
};

watch(() => props.formData, handleFormDataChange, { deep: true });

watch(
  () => props.formData?.agent_id,
  (newAgentId) => handleAgentIdChange(newAgentId),
  { immediate: true }
);

defineExpose({
  handleContinue: handleCreateAgentBehaviour,
  isCreating,
  isFormValid,
});

onMounted(async () => {
  loadVoices();
  await fetchCountries(countryOptions, countriesLoading);
});

onBeforeUnmount(async () => {
  debouncedPublish.cancel();
  await handlePublish();
});
</script>

<style scoped>
.manage_variables_container {
  align-self: flex-start !important;
  width: fit-content !important;
}
</style>
